import React, { useEffect } from 'react';
import { useMessagingStore, useConversations } from '../../stores/messagingStore';
import ConversationItem from './ConversationItem';
import { useTranslation } from 'react-i18next';

interface ConversationListProps {
    onSelectConversation?: (conversationId: string) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({ onSelectConversation }) => {
    const { t } = useTranslation();
    const conversations = useConversations();
    const { loading, hasMoreConversations, page, fetchConversations } = useMessagingStore();

    useEffect(() => {
        fetchConversations(1);
    }, [fetchConversations]);

    const handleLoadMore = () => {
        if (!loading && hasMoreConversations) {
            fetchConversations(page + 1);
        }
    };

    if (loading && conversations.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
            </div>
        );
    }

    if (conversations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-gray-500">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-16 w-16 mb-4 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                </svg>
                <p className="text-lg font-medium text-gray-600 mb-2">
                    {t('messaging.noConversations', 'No conversations yet')}
                </p>
                <p className="text-sm text-gray-400 text-center">
                    {t('messaging.startConversation', 'Start a conversation with someone to practice your English!')}
                </p>
            </div>
        );
    }

    return (
        <div className="divide-y divide-gray-100">
            {conversations.map((conversation) => (
                <ConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    onClick={() => onSelectConversation?.(conversation.id)}
                />
            ))}

            {hasMoreConversations && (
                <div className="px-4 py-3">
                    <button
                        onClick={handleLoadMore}
                        disabled={loading}
                        className="w-full py-2 text-sm text-primary-500 hover:text-primary-600 font-medium disabled:opacity-50"
                    >
                        {loading ? (
                            <span className="flex items-center justify-center">
                                <span className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent mr-2" />
                                {t('common.loading', 'Loading...')}
                            </span>
                        ) : (
                            t('messaging.loadMore', 'Load more conversations')
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ConversationList;
